import { useState } from 'react';
import { Loader2, Send } from 'lucide-react';
import { Button } from '../../ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../ui/alert-dialog';
import api from '../../../services/api';
import { toast } from 'sonner';

/**
 * One-click "send payment reminder to all unpaid parents" for a tournament.
 *
 * Backend resolves recipients automatically (status != cancelled,
 * payment_status != paid). Renders the seeded
 * "Tournament Payment Reminder" email template with per-registration
 * variables. We gate the action behind a confirmation so admins don't
 * accidentally email a hundred parents twice.
 */
const SendPaymentRemindersButton = ({ tournamentId, unpaidCount = null, onSent }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleConfirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/tournaments/${tournamentId}/send-payment-reminders`,
      );
      const sent = data?.sent || 0;
      const examined = data?.registrations_examined || 0;
      const errors = data?.errors || [];

      if (sent > 0) {
        toast.success(
          `Sent ${sent} payment reminder${sent === 1 ? '' : 's'} (${examined} unpaid registration${examined === 1 ? '' : 's'} examined).`,
        );
      } else {
        toast.info(data?.message || 'No reminders were sent.');
      }
      if (errors.length > 0) {
        toast.error(`${errors.length} email${errors.length === 1 ? '' : 's'} failed — check server logs.`);
      }
      setOpen(false);
      onSent?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send payment reminders');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="text-amber-700 border-amber-300 hover:bg-amber-50"
          disabled={unpaidCount === 0}
          title={
            unpaidCount === 0
              ? 'No unpaid registrations'
              : 'Send the Tournament Payment Reminder email to every parent whose registration is not yet paid.'
          }
          data-testid="send-payment-reminders-btn"
        >
          <Send className="h-4 w-4 mr-1" />
          Send Payment Reminder{unpaidCount && unpaidCount > 0 ? ` (${unpaidCount})` : ''}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Send payment reminders?</AlertDialogTitle>
          <AlertDialogDescription>
            This will email the <strong>Tournament Payment Reminder</strong> template
            to every parent whose registration in this tournament is{' '}
            <strong>not yet paid</strong>
            {unpaidCount != null
              ? ` (${unpaidCount} unpaid registration${unpaidCount === 1 ? '' : 's'} right now).`
              : '.'}
            <br />
            <br />
            Already-paid and cancelled registrations are skipped automatically.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy} data-testid="send-payment-reminders-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); handleConfirm(); }}
            disabled={busy}
            data-testid="send-payment-reminders-confirm"
          >
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
            Send reminders
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default SendPaymentRemindersButton;
